/*
 * etusivu-kaista.js — the ONE script the front page gets.
 *
 * docs/AGENT/editable-sections-standard.md §1 rule 4 says "native HTML first,
 * and no JavaScript", and it says the blog's exception "does not travel" —
 * adding a second script means making the argument again rather than citing
 * the first one. So here is the argument, made once, for one file.
 *
 * THE ARGUMENT. Three bands chosen by Susanna on 2026-09-04 are horizontal
 * rails: sinulle (nine situations on a card strip), kokemukset (one
 * testimonial at a time) and blogi on narrow screens (a card carousel). A rail
 * is native — `overflow-x: auto` plus `scroll-snap-type` needs no script, and
 * every one of these rails is fully usable with this file blocked: it scrolls
 * with a finger, a trackpad, a mouse wheel, the keyboard and a screenreader's
 * own scrolling. What is NOT native is the part the client asked for by name:
 *
 *   "Swiping in browser is not very solid since when swiping right to left,
 *    the browser might think 'that was a back action, going back to the
 *    previous page'. So we need to take care of that."
 *
 * That is a real defect (the iOS/Android edge-swipe back gesture) and the
 * remedy has two halves. The CSS half is `overscroll-behavior-x: contain` and
 * keeping the rail inside `.eb`'s inline padding so it never reaches the
 * screen edge where the gesture lives — both are in assets/etusivu.css. The
 * other half is giving the rail real, visible, 44px BUTTONS so a reader never
 * has to swipe at all. Buttons that scroll a container cannot be built from
 * HTML: an `<a href="#card-3">` moves the vertical scroll position of the page
 * as well, and pushes a history entry per card. Chrome's CSS-only
 * `::scroll-button()` would do it natively and is Chromium-only today — the
 * same test the blog's TOC was held to, with the same answer: a control that
 * exists in one browser family is worse than one that exists everywhere.
 *
 * So this file exists to make an already-working rail SAFE and OBVIOUS. It is
 * decoration over content that is already complete.
 *
 * WHAT IT DOES, and nothing else:
 *   1. reveals the prev/next/dot controls the section already rendered (they
 *      ship with `hidden`, so a reader without JS never sees a dead button);
 *   2. scrolls the rail when one is pressed;
 *   3. keeps `disabled` and `aria-current` truthful as the rail scrolls;
 *   4. drops a `data-kaista-alku`/`-loppu` hint on the rail so CSS can fade
 *      the edge that has more content behind it — the scroll affordance, and a
 *      one-shot `data-kaista-js` so CSS can retire the written hint;
 *   5. keeps the rail's `tabindex` truthful — a scroll region earns a focus
 *      stop, a grid that cannot scroll does not.
 *
 * ⚠️ THE 990px SCROLL-CONTAINER TRAP DOES NOT APPLY HERE, and that is why this
 * file imports nothing. Horizon scrolls `.page-wrapper` rather than the
 * document above 990px, which breaks `window.scrollY` and a default-root
 * IntersectionObserver — but every measurement below is `scrollLeft` on the
 * rail element itself, which is the same number in both layouts. No import
 * means no dependency on @theme/*; `type="module"` is kept anyway, because it
 * is what guarantees the browser evaluates the file exactly once however many
 * of the three bands are on the page.
 *
 * CONSTRAINTS, all inherited from the blog's exception and all binding:
 *   - it does nothing if its elements are absent;
 *   - everything works with the script blocked;
 *   - no library, no polyfill, no third-party request;
 *   - motion is opt-in (§6.1 item 9) — a programmatic scroll is smooth only
 *     when the reader has not asked for less motion, checked live rather than
 *     once at load, because the OS setting can change mid-session;
 *   - no customer-visible string is created here. Every label, including every
 *     `aria-label`, is rendered by the section from a schema setting, because
 *     a string born in JavaScript is a string Susanna cannot edit
 *     (editable-sections-standard §3);
 *   - CLAUDE.md JS conventions: `globalThis` over `window`, `Number.*` over the
 *     globals, `element.remove()`.
 */

const doc = globalThis.document;

const REDUCED = globalThis.matchMedia?.('(prefers-reduced-motion: reduce)');

/** Sub-pixel scroll positions are normal (zoom, fractional gaps, RTL), so
 *  every end-of-rail comparison needs slack or the "next" button never
 *  disables at the far end.
 *
 *  Raised from 2 to 8 on 2026-09-15. The settled card's `scale: 1.03`
 *  contributes to the rail's scrollable overflow on the inline axis too, so
 *  `scrollWidth` shrinks by up to ~5px on a 336px card for the length of every
 *  un-zoom — and three things here read it every frame, including the one that
 *  switches the rail's edge-fade mask. The desktop spacers and the mobile
 *  gutters absorb it at most widths, but not where `max(0px, …)` clamps the
 *  spacer to zero, around 750–900px. Eight pixels covers the whole excursion. */
const SLACK = 8;

/* --- The step animation ----------------------------------------------------
 * `scrollTo({behavior: 'smooth'})` was doing this until 2026-09-15 and the
 * client's note on it was that it "feels a bit too snappy for the tone and
 * vibe of the site". That is not a tuning complaint the browser can answer:
 * the duration and the curve of native smooth scrolling are UA-controlled and
 * exposed to no API. Chrome's is roughly 300ms with an ease-in-out that starts
 * fast. So the tween is ours.
 *
 * 820ms on a smootherstep curve, for a ONE-CARD step. Both numbers were
 * revised on 2026-09-15 after the first attempt (620ms, ease-out cubic) still
 * read as jarring. A longer move scales this up sub-linearly — see the √n
 * note in tweenTo(), which is what lets a wrap traverse the whole rail
 * without either blurring or dragging.
 *
 * The DURATION was the smaller half of that. The curve was the larger half, and
 * the reason is not obvious: an ease-out cubic is at its FASTEST in its very
 * first frames — it covers roughly a third of the distance in the first tenth
 * of the time. That start is a lurch, and on this rail it is amplified, because
 * the cylinder below is scroll-linked: every card's rotation, opacity and now
 * background are being driven by the same scroll position, so an abrupt start
 * jerks five cards at once rather than one scrollbar.
 *
 * Smootherstep (6t⁵ − 15t⁴ + 10t³) has zero first AND second derivative at both
 * ends — it starts from rest, accelerates, and comes to rest, with no
 * discontinuity in acceleration at either end. That second-derivative property
 * is the part an ease-out cubic lacks and the part the eye reads as "smooth"
 * rather than merely "slow". It is the standard curve for exactly this in
 * graphics work, and it costs one line.
 *
 * The trade accepted knowingly: a curve that starts from rest feels very
 * slightly less responsive to the press than one that leaps. At 820ms with the
 * arrows staying live throughout, that is the right side of the trade for a
 * page whose whole register is unhurried.
 *
 * Reduced motion is not "a shorter tween" — it is no tween at all (§6.1 item
 * 9), so the scroll position is assigned in one go. Checked at call time
 * because the OS preference can change while the page is open. */
/* How long the rail must be still before it counts as settled. Long enough to
 * survive the gaps between scroll events during a flick, short enough that the
 * zoom feels like a consequence of arriving rather than a delayed effect. */
const SETTLE_DELAY = 140;

const DURATION = 820;
const EASE = (t) => t * t * t * (t * (t * 6 - 15) + 10);

class EtusivuKaista {
  /** @param {HTMLElement} root */
  constructor(root) {
    this.root = root;
    this.rail = root.querySelector('[data-kaista-rail]');
    this.nav = root.querySelector('[data-kaista-nav]');
    this.prev = root.querySelector('[data-kaista-prev]');
    this.next = root.querySelector('[data-kaista-next]');
    this.dots = [...root.querySelectorAll('[data-kaista-dot]')];

    if (!this.rail) return;

    /* Both modes now move exactly ONE CARD per press. They still differ in how
       far that is: 'slide' (kokemukset) shows one quote at a time, so one card
       IS the rail's width; 'strip' (sinulle, blogi) shows several, so one card
       is one item's pitch.

       ⚠️ Changed 2026-09-15, client call, and the note it replaces argued the
       opposite — that a nine-card strip advancing one card per press "would
       need eight presses". That is true and is no longer considered a cost: a
       page-at-a-time press on a rail showing 3.5 cards teleports the reader
       past cards they were mid-way through reading, and on a strip of short
       independent statements (no order, no progression — see the section
       header comment) there is nothing to be gained by arriving anywhere fast.
       Eight presses through nine situations is the reader choosing to see them
       all, one at a time, which is the band's whole purpose. */
    this.mode = root.dataset.kaistaMode === 'slide' ? 'slide' : 'strip';

    this.items = [...this.rail.querySelectorAll('[data-kaista-item]')];
    this.index = 0;

    /* The index a running tween is heading for, or null when nothing is in
       flight. Presses read THIS rather than `index` so that a reader pressing
       faster than 620ms advances one card per press: `index` still reports
       where the rail physically is, which mid-tween is the card we are leaving.
       Cleared when the tween lands and when the reader takes over by hand. */
    this.pending = null;

    /* Alignment is read from CSS rather than hard-coded, because it differs by
       breakpoint: the sinulle band centre-snaps below 750px and start-snaps
       above it (sections/etusivu-sinulle.liquid). Everything that asks "which
       card is current" or "where does card n sit" has to agree with whichever
       is in force, or the stepper highlights the wrong segment. */
    this.align = 'start';

    /* Set when the alignment could have changed — construction and any resize,
       which is the only way a breakpoint is crossed. Reading it per frame cost
       a forced style recalc on every scroll frame for a value that changes
       about twice in a session. */
    this.alignStale = true;

    /* Opt-in per band, from the markup. Wrapping is right for a ring of
       independent statements (the sinulle situations: no order, no
       progression, so "after the last" is a meaningful place to be) and wrong
       for a set the reader is meant to feel the ends of. kokemukset and blogi
       do not set it and keep their disabling arrows. */
    this.wrap = root.hasAttribute('data-kaista-wrap');

    this.onScroll = this.onScroll.bind(this);
    this.paint = this.paint.bind(this);
    this.onResize = () => {
      this.alignStale = true;
      this.paint();
    };

    this.bind();
    this.paint();

    /* Nothing has moved yet, so the rail starts settled — otherwise the first
       card would sit un-zoomed until the reader touched something. */
    this.settle();
  }

  /* --- Moving vs settled ----------------------------------------------------
   * ⚠️ WHY THIS EXISTS RATHER THAN LIVING IN THE SCROLL-DRIVEN ANIMATION.
   * The cylinder is a `view(inline)` animation: every property in it is
   * recomputed from the scroll position on every frame. That is right for the
   * rotation, the opacity and the surface colour — they ARE the drum turning.
   * It was wrong for the zoom and the lift, because those are not a property of
   * the surface, they are a statement about WHICH CARD YOU ARE ON. Tied to
   * scroll, that statement was being made and unmade continuously: every card
   * swelled and shrank as it crossed the middle, so the band never stopped
   * moving even when the reader had.
   *
   * So the zoom is keyed off stillness instead. `data-kaista-settled` goes on
   * the wrapper when the rail has not scrolled for SETTLE_DELAY, and comes off
   * the moment it moves again. A programmatic tween emits scroll events like
   * any other scroll, so it needs no special case — it settles when it lands.
   */
  markMoving() {
    this.root.removeAttribute('data-kaista-settled');
    globalThis.clearTimeout(this.settleTimer);
    this.settleTimer = globalThis.setTimeout(() => {
      /* ⚠️ A RUNNING TWEEN IS NOT STILLNESS, however quiet the scroll events
         have gone. On the tail of the smootherstep curve the per-frame delta
         approaches zero, so `scrollLeft` can round to the same integer for
         several frames and the browser stops firing `scroll` — the timer then
         fires MID-TWEEN, promotes a card that is still travelling, and the next
         real scroll event demotes it again. That flap is visible as a jitter.
         Re-arming instead of settling costs one more 140ms wait and cannot
         promote early. */
      if (this.raf !== null && this.raf !== undefined) {
        this.markMoving();
        return;
      }
      this.settle();
    }, SETTLE_DELAY);
  }

  bind() {
    this.rail.addEventListener('scroll', this.onScroll, { passive: true });

    /* A tween owns `scrollLeft` frame by frame, so a reader who starts
       scrolling by hand mid-flight would be fighting it for the rest of the
       620ms. These three are every way that can start — finger, wheel or
       trackpad, and a scrollbar/pointer drag — and any of them hands control
       straight back. Passive: none of them is cancelled, only observed. */
    for (const type of ['pointerdown', 'wheel', 'touchstart']) {
      this.rail.addEventListener(type, () => this.cancelTween(), { passive: true });
    }

    /* The rail is a focusable scroll region (tabindex="0" in the Liquid), so
       the browser already gives it arrow-key scrolling. These two only make
       the step match what the buttons do, so keyboard and pointer agree. */
    this.rail.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') {
        this.step(1);
        event.preventDefault();
      } else if (event.key === 'ArrowLeft') {
        this.step(-1);
        event.preventDefault();
      } else if (event.key === 'Home') {
        this.goTo(0);
        event.preventDefault();
      } else if (event.key === 'End') {
        this.goTo(this.items.length - 1);
        event.preventDefault();
      }
    });

    this.prev?.addEventListener('click', () => this.step(-1));
    this.next?.addEventListener('click', () => this.step(1));

    for (const [i, dot] of this.dots.entries()) {
      dot.addEventListener('click', () => this.goTo(i));
    }

    if (typeof globalThis.ResizeObserver === 'function') {
      this.observer = new globalThis.ResizeObserver(() => {
        this.alignStale = true;
        this.paint();
      });
      this.observer.observe(this.rail);
    } else {
      globalThis.addEventListener('resize', this.onResize, { passive: true });
    }
  }

  /* Called when the theme editor throws this section's DOM away. Without it the
     observer keeps watching a detached rail for the rest of the session. */
  destroy() {
    globalThis.clearTimeout(this.settleTimer);
    this.cancelTween();
    this.observer?.disconnect();
    globalThis.removeEventListener('resize', this.onResize);
  }

  /* The editor's "select a block" — Susanna clicking "Tilanne 7" in the
     sidebar. Shopify scrolls the page to the band; only the rail knows how to
     bring a card that is off to the side into view. */
  reveal(node) {
    const index = this.items.indexOf(node.closest('[data-kaista-item]'));
    if (index >= 0) this.goTo(index);
  }

  /** The distance between two consecutive items' leading edges \u2014 card width
   *  plus gap, in one measurement instead of two, and correct whatever the gap
   *  token resolves to at this viewport.
   *
   *  \u26a0\ufe0f THIS METHOD WAS DELETED AND THEN CALLED ANYWAY, which shipped a broken
   *  carousel to live on 2026-09-15. The stepping rewrite replaced a
   *  distance-based `stepDistance()` with the item-position model below and
   *  removed it; the wrap commit then reintroduced a call to it in tweenTo()
   *  for the duration scaling. `node --check` passes that happily \u2014 it checks
   *  syntax, not whether a method exists \u2014 and theme-check does not read JS at
   *  all, so nothing in the pipeline caught a guaranteed TypeError on the first
   *  arrow press. Both rails threw on every press until it was restored.
   *
   *  Returns 0 when it cannot measure, so callers decide the fallback rather
   *  than silently receiving a plausible-looking wrong number. */
  pitch() {
    const [first, second] = this.items;
    if (!first || !second) return 0;
    const gap = second.getBoundingClientRect().left - first.getBoundingClientRect().left;
    return gap > 1 ? gap : 0;
  }

  /** The scrollLeft at which item `index` sits exactly where the rail's own
   *  scroll-snap alignment wants it.
   *
   *  ⚠️ EVERYTHING IS AN ITEM POSITION NOW, not a distance. Until 2026-09-15
   *  a press was `scrollBy(one pitch)` — card width plus gap — which is right
   *  only while every card is the same width and the rail snaps to `start`.
   *  It is wrong the moment a rail centre-snaps, and it accumulates rounding
   *  over a long strip. Asking the item where it wants to be is exact by
   *  construction, at both alignments, at any card width, and it is what lets
   *  the tween below switch snapping off while it runs: the position it lands
   *  on IS a snap position, so restoring `scroll-snap-type` afterwards moves
   *  nothing.
   *
   *  Bounding rects plus the current scroll position rather than `offsetLeft`,
   *  which is measured against the nearest positioned ancestor and that is not
   *  reliably the rail. */
  positionOf(index, railRect) {
    const item = this.items[index];
    if (!item) return this.rail.scrollLeft;

    /* The rail's own rect is the same for every item in a frame, so paint()
       measures it once and passes it in. Re-reading it per item turned one
       layout read into N — eighteen rects and nine scroll reads per frame on a
       nine-card rail, every frame of every scroll. */
    const rail = railRect ?? this.rail.getBoundingClientRect();
    const rect = item.getBoundingClientRect();

    if (this.align === 'center') {
      return this.rail.scrollLeft + (rect.left + rect.width / 2) - (rail.left + rail.width / 2);
    }

    return this.rail.scrollLeft + rect.left - rail.left;
  }

  /** One press, one card \u2014 in both modes. `pending` is what makes a reader
   *  who presses faster than the tween advance one card per press: `index`
   *  reports where the rail physically is, which mid-flight is the card being
   *  left behind, so counting from it would swallow the second press. */
  step(direction) {
    const last = this.items.length - 1;
    const from = this.pending ?? this.index;
    const raw = from + direction;

    /* ⚠️ A WRAP TRAVELS. It used to cut — the rail was assigned straight to the
       far end with no tween — on the reasoning that dragging across eight cards
       would read as a rewind. Client call 2026-09-15 reversed that, and the
       reversal is right for a reason the first version missed: a teleport
       destroys the one thing the cylinder exists to convey, which is that the
       cards are arranged along a continuous surface. Cutting to the far end
       makes them look like unrelated slides being swapped.
       
       What makes the traverse work rather than blur is the distance-scaled
       duration in tweenTo(), not the tween itself. That is the part the
       original objection was actually about. */
    if (this.wrap && (raw < 0 || raw > last)) {
      const wrapped = raw < 0 ? last : 0;
      this.pending = wrapped;
      this.goTo(wrapped);
      return;
    }

    const next = Math.min(last, Math.max(0, raw));
    this.pending = next;
    this.goTo(next);
  }

  goTo(index) {
    if (!this.items[index]) return;
    this.tweenTo(this.positionOf(index));
  }

  /** Ours rather than `behavior: 'smooth'` — see DURATION at the top of the
   *  file for why the browser's own curve could not be tuned.
   *
   *  ⚠️ SNAPPING IS OFF FOR THE LENGTH OF THE TWEEN. A tween owns `scrollLeft`
   *  frame by frame, and under `scroll-snap-type: x mandatory` (the sinulle
   *  band on mobile, and every slide-mode rail) the engine re-snaps after each
   *  assignment — the two fight and the movement stutters. Switching it off is
   *  safe here ONLY because positionOf() lands on a real snap position, so the
   *  restore at the end is a no-op rather than a correction the reader sees.
   *  The style is removed rather than set back to a value, so the stylesheet
   *  stays the single source of truth for which mode each rail is in. */
  tweenTo(left) {
    this.cancelTween();

    const max = this.rail.scrollWidth - this.rail.clientWidth;
    const to = Math.max(0, Math.min(max, left));
    const from = this.rail.scrollLeft;

    /* No tween at all for a reader who asked for less motion, and none for a
       move too small to see — which is also the guard that stops a tween being
       started by a press at either end of a non-wrapping rail. */
    if (REDUCED?.matches || Math.abs(to - from) < 1) {
      this.rail.scrollLeft = to;
      this.pending = null;
      return;
    }

    /* ⚠️ DURATION SCALES WITH DISTANCE, AND SUB-LINEARLY. A wrap crosses eight
       cards where a step crosses one; at a flat 820ms that is eight times the
       speed, which is the blur the earlier cut-instead-of-tween was avoiding.
       Scaling linearly is the other failure — 6.5 seconds to cross a
       nine-card rail, and the reader has left.

       √n is the standard compromise: a long traverse takes longer but never
       proportionally longer, so it reads as one deliberate sweep rather than
       either a teleport or a trek. Capped at 1.9× (≈ 1.55s) so a very long rail
       cannot run away — √8 alone would already be 2.3s.

       Measured against the rail's own pitch rather than a card count, so it
       stays correct at both snap alignments and at any card width. */
    const span = Math.abs(to - from);
    const pitch = this.pitch() || span || 1;
    const cards = Math.max(1, span / pitch);
    const duration = DURATION * Math.min(1.9, Math.sqrt(cards));

    this.rail.style.scrollSnapType = 'none';

    const started = globalThis.performance.now();
    const frame = (now) => {
      const t = Math.min(1, (now - started) / duration);
      this.rail.scrollLeft = from + (to - from) * EASE(t);

      if (t < 1) {
        this.raf = globalThis.requestAnimationFrame(frame);
        return;
      }

      this.raf = null;
      const landed = this.pending;
      this.pending = null;

      /* ⚠️ THE HANDOVER BACK TO SNAPPING IS THE DELICATE PART, and doing it in
         this frame was the roughness the client saw at the end of every slide.
         
         Two separate problems, both fixed here.

         FIRST, the target is RE-MEASURED rather than trusted. `to` was computed
         when the press happened, up to 1.5 seconds earlier; anything that
         changed layout in between — a card settling, a font finishing, a
         scrollbar appearing — leaves it a pixel or two off where the card
         actually sits now. Asking the item again costs one rect read and
         guarantees we hand over a position that IS a snap position. Deliberately
         not rounded: Blink stores scroll offsets at 1/64px, so rounding to an
         integer would reintroduce up to half a pixel of exactly the error this
         is removing.

         SECOND, snapping comes back A FRAME LATER. Removing the inline
         `scroll-snap-type` in the same frame as the final scroll assignment
         asks the engine to re-snap from a position it has not committed yet, so
         it snaps from the second-to-last frame and corrects — an instant jump
         of a few pixels, always at the same moment, which is precisely what
         "the slide interrupts at the end" describes. One frame of daylight lets
         the final position land first, so there is nothing left to correct. */
      if (landed !== null && landed !== undefined) {
        /* ⚠️ GATED, and the gate is the point. This correction was added on
           2026-09-15 to absorb layout drift during a long tween, and it did not
           fix the end-of-slide roughness it was aimed at — two independent
           reviews then landed on it as the likely CAUSE, which fits: ungated it
           is an unconditional sub-pixel scroll WRITE at exactly the moment the
           slide ends, and any scroll write re-poses all nine scroll-driven
           animations.
           
           The residual is rarely zero. positionOf() mixes `scrollLeft` (the
           engine's stored fractional offset, 1/64px in Blink) with
           getBoundingClientRect() (derived from the painted offset), and those
           two need not agree to the last fraction. So the guard: correct real
           drift, ignore the noise that measuring introduces. Half a pixel is
           below what anyone can see and far above the disagreement between
           those two sources. */
        const delta = this.positionOf(landed) - this.rail.scrollLeft;
        if (Math.abs(delta) > 0.5) this.rail.scrollLeft += delta;
      }

      globalThis.requestAnimationFrame(() => {
        this.rail.style.removeProperty('scroll-snap-type');

        /* Start the settle clock from the true end of the tween. Without this
           the rail would wait for a scroll event that may never come — the last
           frame often moves nothing. */
        this.markMoving();
      });
    };

    this.raf = globalThis.requestAnimationFrame(frame);
  }

  /** The rail has stopped. This is the only place the active item is marked,
   *  so the subtree invalidation it causes lands when nothing is animating
   *  rather than in the middle of a slide. */
  settle() {
    /* ⚠️ ONLY THE CARDS THAT CHANGE ARE TOUCHED. Toggling all nine every time
       wrote to eight elements whose value was already correct, and each of
       those is an ANCESTOR of a card running a scroll-driven animation — the
       same subtree-invalidation cost the comment in paint() moved this call
       here to avoid. Writing only the difference makes it two elements. */
    if (this.activeItem !== this.items[this.index]) {
      this.activeItem?.removeAttribute('data-kaista-active');
      this.activeItem = this.items[this.index];
      this.activeItem?.setAttribute('data-kaista-active', '');
    }

    if (!this.root.hasAttribute('data-kaista-settled')) {
      this.root.setAttribute('data-kaista-settled', '');
    }
  }

  /** Hands the rail back, wherever it happens to be. Snapping comes back on
   *  with it, so a rail abandoned between two cards settles onto one under
   *  mandatory snapping rather than being left stranded. */
  cancelTween() {
    if (this.raf === null || this.raf === undefined) return;
    globalThis.cancelAnimationFrame(this.raf);
    this.raf = null;
    this.pending = null;
    this.rail.style.removeProperty('scroll-snap-type');
  }

  onScroll() {
    this.markMoving();
    if (this.ticking) return;
    this.ticking = true;
    globalThis.requestAnimationFrame(() => {
      this.ticking = false;
      this.paint();
    });
  }

  paint() {
    const { scrollLeft, scrollWidth, clientWidth } = this.rail;
    const overflows = scrollWidth - clientWidth > SLACK;

    /* One-shot, and it means one thing: this script ran. The written hint is
       retired from CSS off this attribute rather than off the nav's `hidden`,
       because `hidden` is also set when the rail simply does not overflow —
       and a hint saying "scroll sideways" under a rail that cannot is worse
       than no hint at all. */
    if (!this.flagged) {
      this.root.setAttribute('data-kaista-js', '');
      this.flagged = true;
    }

    /* A scroll region needs a focus stop so a keyboard reader can reach
       content that is scrolled out of view; a container that cannot scroll —
       blogi's rail above 750px is a grid, and any rail whose items fit — is
       just a dead tab stop that announces itself for nothing. Server-rendered
       as tabindex="0" so the no-script case keeps the stop it needs. */
    if (overflows) this.rail.setAttribute('tabindex', '0');
    else this.rail.removeAttribute('tabindex');

    /* The controls exist in the DOM from the server but ship `hidden`. They
       are revealed only when there is genuinely something to scroll, so a rail
       whose items happen to fit (three FAQ-length quotes on a wide desktop, a
       blog with one article) does not grow a pair of dead arrows. */
    this.nav?.toggleAttribute('hidden', !overflows);

    /* The edge fades in assets/etusivu.css key off these. Nothing here writes
       a style: the appearance stays in the stylesheet where the reduced-motion
       and theming rules already live. */
    this.root.toggleAttribute('data-kaista-alku', overflows && scrollLeft > SLACK);
    this.root.toggleAttribute(
      'data-kaista-loppu',
      overflows && scrollLeft < scrollWidth - clientWidth - SLACK
    );

    /* Which alignment is in force, read from the first item's computed style
       rather than assumed. It is a media query away from changing (sinulle
       centre-snaps below 750px, start-snaps above), and every answer below
       depends on it. Read here, in the one method that already reads layout,
       so positionOf() and step() can use it without a style flush of their
       own. `scroll-snap-align` may be two values; the inline one is first. */
    if (this.alignStale) {
      const [first] = this.items;
      if (first) {
        this.align = globalThis.getComputedStyle(first).scrollSnapAlign.split(' ')[0];
      }
      this.alignStale = false;
    }

    /* The active item — the one sitting closest to where the rail's snapping
       wants an item to sit. Computed whether or not there is a stepper or
       dots, because slide mode disables its arrows from this index rather than
       from the scroll extents.

       ⚠️ Measured against the ALIGNED position, not against the rail's leading
       edge, and the difference is the whole stepper. Under centre snapping the
       item nearest the leading edge is the one peeking in the left gutter —
       the card the reader has just left — so a leading-edge test lights the
       segment one behind the card actually in front of them. */
    const railRect = this.rail.getBoundingClientRect();
    let best = 0;
    let bestDistance = Number.POSITIVE_INFINITY;
    for (let i = 0; i < this.items.length; i += 1) {
      const distance = Math.abs(this.positionOf(i, railRect) - scrollLeft);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = i;
      }
    }
    this.index = best;

    /* ⚠️ DELIBERATELY NOT WRITTEN HERE. `data-kaista-active` used to be toggled
       on every paint, which meant it flipped from one <li> to the next as the
       rail crossed the midpoint between two cards — the exact middle of every
       slide, which is exactly where the client saw the slide "interrupt",
       always at the same spot.
       
       The cost is not the attribute write, it is where the attribute LIVES.
       The settled rules select `[data-kaista-active] .etusivu-sinulle__card`,
       so the mutated node is an ANCESTOR of an element with a running
       scroll-driven animation. Mutating an ancestor invalidates style for the
       whole subtree beneath it, forcing the card's keyframes to be re-resolved
       mid-flight — twice, once for the card losing the attribute and once for
       the card gaining it. Visually it changed nothing, because nothing is
       promoted while moving. It was pure cost, paid at the least forgiving
       moment.
       
       It is written in settle() instead, which is the only moment anything
       reads it. `this.index` is still computed every frame, because the stepper
       does need to fill continuously — but those are buttons in a different
       subtree with no animation on them, so writing to them is cheap. */

    /* ⚠️ Slide mode disables from the INDEX, not from scrollLeft against the
       scroll extents, and the difference is visible: with
       `scroll-snap-type: x mandatory` the last slide's snap position is a
       handful of pixels short of the maximum scrollLeft (the rail's own
       padding and the trailing gap), so an extent test left "next" enabled on
       the final quote — pressing it snapped straight back and looked broken.
       Strip mode keeps the extent test, because there the last item is
       deliberately allowed to sit part-scrolled. */
    if (this.wrap) {
      /* A wrapping rail has no ends to disable at: both directions always lead
         somewhere. Written as an assignment rather than skipped, so a rail that
         had its arrows disabled before `wrap` was turned on cannot keep them. */
      if (this.prev) this.prev.disabled = false;
      if (this.next) this.next.disabled = false;
    } else if (this.mode === 'slide') {
      if (this.prev) this.prev.disabled = best <= 0;
      if (this.next) this.next.disabled = best >= this.items.length - 1;
    } else {
      if (this.prev) this.prev.disabled = scrollLeft <= SLACK;
      if (this.next) this.next.disabled = scrollLeft >= scrollWidth - clientWidth - SLACK;
    }

    if (this.dots.length > 0) {
      for (const [i, dot] of this.dots.entries()) {
        /* aria-current, not aria-selected: these are not tabs and the rail is
           not a tabpanel. The mock's `role="tablist"` was dropped in the
           2026-09-03 audit for the same reason. */
        if (i === best) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      }
    }
  }
}

/* ---------------------------------------------------------------- init ----
 * ⚠️ A bare top-level `querySelectorAll` is NOT enough, and this is the one
 * defect the 2026-09-04 audit found that attacks the thing the whole
 * schema-first structure exists for. In the theme editor, changing any setting
 * or block re-renders that section through the Section Rendering API and
 * REPLACES its DOM. The replacement markup contains this file's <script> tag,
 * but a script inserted as parsed markup never executes — and a module at a
 * URL already in the module map would not re-evaluate even if it did. So every
 * instance would stay bound to nodes that no longer exist: Susanna edits her
 * situations band, the arrows and dots vanish (the nav keeps its server-side
 * `hidden`), the edge fade freezes, and only a full page reload brings them
 * back. Adding a band from the section picker was worse — it was wired never.
 *
 * Horizon's own sections survive this because they are custom elements, which
 * re-upgrade automatically. This one is not, so it listens instead. The
 * WeakMap is the guard: `shopify:section:load` also fires for sections that
 * were never torn down, and double-binding a rail doubles every scroll step.
 * ------------------------------------------------------------------------ */
const instances = new WeakMap();

/* `roots` rather than a bare querySelectorAll: on shopify:section:load the
   event target is the section element, and a band could legitimately put the
   attribute on that element itself, where a descendant query would miss it. */
const roots = (scope) => [
  ...(scope.matches?.('[data-kaista]') ? [scope] : []),
  ...scope.querySelectorAll('[data-kaista]'),
];

const mount = (scope) => {
  for (const root of roots(scope)) {
    if (instances.has(root)) continue;
    instances.set(root, new EtusivuKaista(root));
  }
};

const unmount = (scope) => {
  for (const root of roots(scope)) {
    instances.get(root)?.destroy();
    instances.delete(root);
  }
};

mount(doc);

/* All three listeners are editor-only in practice — these events do not fire on
 * a storefront visit — so a normal reader pays nothing for them. */
doc.addEventListener('shopify:section:load', (event) => mount(event.target));
doc.addEventListener('shopify:section:unload', (event) => unmount(event.target));
doc.addEventListener('shopify:block:select', (event) => {
  const root = event.target.closest('[data-kaista]');
  if (root) instances.get(root)?.reveal(event.target);
});
